import React, { useState, useContext } from "react";
import _ from "lodash";

import Button from "Components/button/Button";
import ButtonIcon from "Components/buttonIcon/ButtonIcon";
import DomainStatusBadge from "Components/domainStatusBadge/DomainStatusBadge";
import { displayFormattedDate } from "Utils/dateFormats";
import { alertSuccess } from "Utils/alerts";
import FullPaginationControls from "Components/fullPaginationControls/FullPaginationControls";
import ResponsiveTable from "Components/responsiveTable/ResponsiveTable";
import TranslatedString from "Components/TranslatedString";
import { Icon, refresh, addMigration, plus, migrations as migrationsIcon, trash } from "Icons/index";
import withConfirmationModal from "Components/withConfirmationModal";
import { t } from "i18next";

import cx from "classnames";
import s from "./listMigrations.scss";

import { useHistory, useLocation } from "react-router-dom";
import { MediaQueriesContext, AuthContext } from "../../App";
import { useApiMutation, useApiQuery } from "../../hooks";

const presetLabels = {
  no_ssl: "Standard no SSL",
  google: "Gmail®",
  office_365: "Office 365",
  exchange_imap: "Microsoft Exchange® (via IMAP)",
  ssl: "Standard-SSL",
};

const migrationStatus = ["validating", "validating resources", "running"];
const showDateStatuses = ["completed_with_errors", "running", "completed", "scheduled", "valid"];

const MigrationListScreen = (props) => {
  const { openConfirmationModal } = props;

  const history = useHistory();
  const location = useLocation();
  const { isMediaMobile, isMobileDevice } = useContext(MediaQueriesContext);
  const { isLoggedUserAdmin } = useContext(AuthContext);
  const [adminExecutingMigration, setAdminExecutingMigration] = useState(null);
  const {
    data: migrations = [],
    pagination,
    refreshData: refreshMigrationsData,
    getData: getMigrationsData,
    isLoading: isMigrationsLoading,
    isRefreshing: isRefreshingMigrations,
    updateData: updateMigrations,
  } = useApiQuery("getMigrations", {
    enablePagination: true,
    autoRefresh: {
      shouldRefresh: (data) => data.some((migration) => migrationStatus.includes(migration.status)),
      interval: 4000,
    },
  });
  const { mutate: deleteMigration } = useApiMutation("deleteMigration", {
    optimistic: (toDelete) => updateMigrations((prev) => prev.filter((item) => item.code !== toDelete)),
  });

  const { mutate: updateMigration } = useApiMutation("editMigration");

  const handleAddNew = () => {
    history.push({ pathname: "/migrations/add", state: { prevPagination: location.search } });
  };

  const handleGoToResources = (code) => {
    history.push({
      pathname: `/migrations/${code}/resources`,
      state: { prevPagination: location.search },
    });
  };

  const handleDeleteMigration = async (code) => {
    await deleteMigration(code);
    await refreshMigrationsData();
    alertSuccess(t("alert.migration.deleted"));
  };

  const handleExecuteMigration = async (code, round) => {
    const execution = round === 1 ? "first" : "second";
    setAdminExecutingMigration(code);
    await updateMigration(code, execution);
    await refreshMigrationsData();
    setAdminExecutingMigration(null);
    alertSuccess(`${execution} execution enqueued`);
  };

  return (
    <div className={s.listMigrations}>
      <div className={s.headerList}>
        <div className={cx("sectionTitle", s.titleList)}>
          <TranslatedString>migrations.list.header.title</TranslatedString>
        </div>

        {!isMediaMobile && (
          <div className={cx("sectionDesc", s.descList)}>
            <TranslatedString>migrations.list.header.dsc</TranslatedString>
          </div>
        )}

        <div className={s.actionsList}>
          <Button docsKey="migrations" color="docs" className={s.btnList} onlyIcon={isMediaMobile}>
            button.docs
          </Button>
          <Button icon={plus} onClick={handleAddNew} onlyIcon={isMediaMobile}>
            button.newMigration
          </Button>
        </div>
      </div>
      <div className={s.contentList}>
        <FullPaginationControls
          showCustomerSelection={isLoggedUserAdmin()}
          className={s.filters}
          paginationInfo={pagination}
          location={location}
          refreshList={() => getMigrationsData()}
          listFilters={["all", "scheduled", "running", "completed_with_errors", "completed", "failed"]}
          listFilterLegends={{
            all: <Icon svg={migrationsIcon} width="0.75rem" height="0.75rem" />,
            scheduled: <DomainStatusBadge status="scheduled" isHealthy showAsPreview />,
            running: <DomainStatusBadge status="updating" showAsPreview />,
            completed: <DomainStatusBadge status="completed" isHealthy showAsPreview />,
            completed_with_errors: <DomainStatusBadge status="completed_with_errors" isHealthy showAsPreview />,
            failed: <DomainStatusBadge status="error" isHealthy showAsPreview />,
          }}
          totals={pagination.totals}
          isLoading={isMigrationsLoading}
          showPaginationFilter={false}
        />

        <div className={s.table}>
          <ResponsiveTable
            verticalOnMobile
            history={history}
            areRowsLoading={isMigrationsLoading && !isRefreshingMigrations}
            location={location}
            addIcon={addMigration}
            addMsg={t("table.row.add.msg.migration")}
            addDesc={t("table.row.add.desc.migration")}
            headers={[
              {
                name: t("table.header.status"),
                size: isMobileDevice ? "2rem" : "4rem",
                center: true,
              },
              { name: t("table.header.owner") },
              { name: t("table.header.code"), size: "6rem" },
              { name: t("table.header.type"), size: "8rem" },
              { name: t("table.header.resources"), size: "4rem" },
              { name: t("table.header.scheduled.migration.first"), size: "8rem" },
              { name: t("table.header.scheduled.migration.second"), size: "8rem" },
              { size: "5rem", hide: !isLoggedUserAdmin() },
              { size: "3rem", center: true },
            ]}
            rows={_.map(migrations, (mig) => {
              let resourcesLink;
              let executeButton;
              if (mig.resources) {
                resourcesLink = (
                  <span
                    className="internalLink"
                    onClick={() => {
                      handleGoToResources(mig.code);
                    }}
                  >
                    {mig.resources}
                  </span>
                );
              }

              let buttonText;
              let round;

              const scheduledFirstExec = mig.status === "scheduled" && mig.status_detail === "first execution";
              const scheduledSecondExec = mig.status === "scheduled" && mig.status_detail === "second execution";
              if (scheduledFirstExec && isLoggedUserAdmin() && !mig.admin_first_execution_override) {
                buttonText = "button.run.migration.first";
                round = 1;
              }

              if (scheduledSecondExec && isLoggedUserAdmin() && !mig.admin_second_execution_override) {
                buttonText = "button.run.migration.second";
                round = 2;
              }

              if (buttonText) {
                executeButton = (
                  <Button
                    onClick={() => {
                      handleExecuteMigration(mig.code, round);
                    }}
                    loading={adminExecutingMigration === mig.code}
                  >
                    {buttonText}
                  </Button>
                );
              }

              const showDate = showDateStatuses.includes(mig.status);

              return (
                <tr key={mig.id}>
                  <td data-label={t("table.header.status")}>
                    <DomainStatusBadge status={mig.status} isHealthy statusDetail={mig.status_detail} />
                  </td>
                  <td data-label={t("table.header.owner")}>{mig.owner}</td>
                  <td data-label={t("table.header.code")}>{mig.code}</td>
                  <td data-label={t("table.header.type")}>{presetLabels[mig.preset] || mig.preset}</td>
                  {/* {mig.status === "invalid" && (
                    <td data-label={t("table.header.resources")} colSpan={isLoggedUserAdmin() ? 4 : 3}>
                      {mig.error_details && mig.error_details[0]}
                    </td>
                  )} */}
                  {<td data-label={t("table.header.resources")}>{resourcesLink}</td>}
                  {mig.status !== "invalid" && (
                    <td data-label={t("table.header.scheduled.migration.first")}>
                      {mig.status === "running" && mig.status_detail === "first execution" ? (
                        <div className={s.executionStatus}>
                          <Icon className="syncIcon" svg={refresh} width={"0.75rem"} height={"0.75rem"} />
                          <span className={s.runningMigration}>
                            <TranslatedString>select.status.migrating</TranslatedString>
                          </span>
                        </div>
                      ) : (
                        showDate && displayFormattedDate(mig.first_execution_date)
                      )}
                    </td>
                  )}
                  {mig.status !== "invalid" && (
                    <td data-label={t("table.header.scheduled.migration.second")}>
                      {mig.status === "running" && mig.status_detail === "second execution" ? (
                        <div className={s.executionStatus}>
                          <Icon className="syncIcon" svg={refresh} width={"0.75rem"} height={"0.75rem"} />
                          <span className={s.runningMigration}>
                            <TranslatedString>select.status.migrating</TranslatedString>
                          </span>
                        </div>
                      ) : showDate && mig.second_execution_date ? (
                        displayFormattedDate(mig.second_execution_date)
                      ) : (
                        "-"
                      )}
                    </td>
                  )}
                  {mig.status !== "invalid" && isLoggedUserAdmin() && <td>{executeButton}</td>}

                  <td>
                    {(isLoggedUserAdmin() || (mig.status != "validating" && mig.status != "running")) && (
                      <ButtonIcon
                        icon={trash}
                        onClick={() =>
                          openConfirmationModal(
                            mig,
                            handleDeleteMigration,
                            <TranslatedString subs={{ migrationCode: mig.code }}>
                              modal.delete.migration.text
                            </TranslatedString>
                          )
                        }
                      />
                    )}
                  </td>
                </tr>
              );
            })}
          />
        </div>
      </div>
    </div>
  );
};

export default withConfirmationModal(MigrationListScreen, {
  title: <TranslatedString>modal.delete.migration.title</TranslatedString>,
  buttonLabel: "button.delete",
});
